import React, { useState } from 'react';
import { message, Modal, Table } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import useHandleError from 'utils/useHandleError';
import apiClient from 'utils/apiClient';
import { useT } from 'hooks/Locale/useT';
import { Button, Select } from 'ui';
import '../styles.scss';

interface AssessmentInstructorsInterface {
    activityFormData: any;
    assessmentSettings: any;
    submission: any;
    loadAssignment: Function;
    setIsSubmitLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isSubmitLoading: boolean;
    session?: any;
}
const mapStateToProps = (state: any) => ({
    session: state.session,
});

const AssessmentInstructors: React.FC<AssessmentInstructorsInterface> = ({
    activityFormData,
    assessmentSettings,
    submission,
    loadAssignment,
    setIsSubmitLoading,
    isSubmitLoading,
    session,
}) => {
    const [newInstructorId, setNewInstructorId] = useState<number>(0);

    const t = useT();
    const [handleError] = useHandleError();
    const { confirm } = Modal;

    const canUpdateInstructors = activityFormData.attemptData.canUpdateInstructors;
    // A graded assessment is never reopened, so an instructor added now cannot submit an evaluation.
    const isCompleted = activityFormData.attemptData.finalAssessment?.isSubmitted;
    const instructorsData = activityFormData.attemptData.instructorsData.filter(
        (item: any) => item.instructorUser,
    );

    const columns = [
        {
            title: t('general.name'),
            key: 'name',
            render: (text: string, record: any, index: number) => {
                return record.instructorUser.name + ' ' + record.instructorUser.surname;
            },
        },
        {
            title: t('general.email'),
            key: 'email',
            render: (text: string, record: any, index: number) => {
                return record.instructorUser.email;
            },
        },
        {
            title: t('general.actions'),
            key: 'actions',
            render: (text: string, record: any, index: number) => {
                return (
                    <>
                        {canUpdateInstructors && (
                            <Button
                                type="link"
                                message="general.delete"
                                disabled={record.isSubmitted}
                                loading={isSubmitLoading}
                                onClick={() => deleteInstructor(record.instructorUser.typeId)}
                                style={{ padding: '0 3px' }}
                            />
                        )}
                    </>
                );
            },
        },
    ];

    const deleteInstructor = async (instructorId: number) => {
        confirm({
            title: t('general.attention'),
            content: t('assessment.deletion_instructor'),
            icon: <ExclamationCircleOutlined />,
            okText: t('general.yes'),
            cancelText: t('general.no'),

            onOk() {
                deleteInstructorSubmit(instructorId);
            },
        });
    };

    const deleteInstructorSubmit = async (instructorId: number) => {
        const parsedValues = {
            instructorId: instructorId,
        };

        try {
            setIsSubmitLoading(true);
            await apiClient.request(
                `/api/v1/courses/assessment-results/delete-instructor/${submission.id}`,
                parsedValues,
                'POST',
            );
            message.success(t('general.success'));
            loadAssignment();
        } catch (error) {
            handleError(error);
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const addInstructor = async () => {
        const parsedValues = {
            instructorId: newInstructorId,
        };

        try {
            setIsSubmitLoading(true);
            await apiClient.request(
                `/api/v1/courses/assessment-results/add-instructor/${submission.id}`,
                parsedValues,
                'POST',
            );
            message.success(t('general.success'));
            setNewInstructorId(0);
            loadAssignment();
        } catch (error) {
            handleError(error);
        } finally {
            setIsSubmitLoading(false);
        }
    };

    return (
        <div className="assessment-overview">
            {canUpdateInstructors ? (
                <>
                    <div className="flex align-center gap-10">
                        {/* Candidates come from this program's access list (right "admin") plus the
                            power admins of the owning organization; remounted so the list refreshes
                            after every add/delete. */}
                        <Select
                            key={instructorsData.length}
                            isFormItem={false}
                            integerKey
                            showSearch
                            selectClassName="request-select"
                            url={`/api/v1/courses/assessment-results/instructor-options/${submission.id}`}
                            value={newInstructorId || undefined}
                            onChange={(value: any) => setNewInstructorId(Number(value))}
                        />
                        <Button
                            type="primary"
                            message="general.add"
                            disabled={!newInstructorId}
                            loading={isSubmitLoading}
                            onClick={() => addInstructor()}
                        />
                    </div>
                    {isCompleted && (
                        <p className="mt-2">{t('assessment.instructors_completed_notice')}</p>
                    )}
                </>
            ) : (
                <p>{t('assessment.instructors_read_only')}</p>
            )}
            <Table columns={columns} dataSource={instructorsData} pagination={false} />
        </div>
    );
};
export default connect(mapStateToProps)(AssessmentInstructors);
